/**
 * payment_addresses.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin — Payment / Wallet Addresses management
 * Route: /admin/payment-addresses
 *
 * Schema:
 * create table public.platform_crypto_wallets (
 *   id serial not null,
 *   symbol varchar(10) not null unique,
 *   name varchar(50) not null,
 *   network varchar(50) not null,
 *   address text not null,
 *   icon varchar(60) not null,
 *   color varchar(10) not null,
 *   is_default boolean not null default false,
 *   is_active boolean not null default true,
 *   created_at timestamptz not null default now(),
 *   updated_at timestamptz not null default now(),
 *   constraint platform_crypto_wallets_pkey primary key (id)
 * );
 */

import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  Wallet, Plus, Trash2, X, Check, ChevronRight,
  Search, RefreshCw, Users, Shield, LogOut, LayoutDashboard,
  Receipt, Copy, UserCheck, HeadphonesIcon, Bell, CandlestickChart,
  BarChart2, ShoppingBag, Menu, AlertCircle, CheckCircle2, XCircle,
  MapPin, CreditCard, Power, PowerOff, Coins, Hash,
} from "lucide-react";

// ─── Design-system styles (mirrors admin_dashboard.jsx / subscriptions.jsx) ──
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{
  --bg-base:#06060f;--bg-surface:#0a0a16;--bg-elevated:#0e0e1c;--bg-card:#111122;--bg-hover:#14142a;
  --border:rgba(255,255,255,0.055);--border-hi:rgba(255,255,255,0.11);--border-glow:rgba(99,102,241,0.3);
  --accent:#6366f1;--accent-light:#818cf8;--accent-dim:rgba(99,102,241,0.13);--accent-glow:rgba(99,102,241,0.22);
  --green:#34d399;--green-dim:rgba(52,211,153,0.13);
  --amber:#fbbf24;--amber-dim:rgba(251,191,36,0.13);
  --rose:#fb7185;--rose-dim:rgba(251,113,133,0.13);
  --cyan:#22d3ee;--cyan-dim:rgba(34,211,238,0.13);
  --purple:#a78bfa;--purple-dim:rgba(167,139,250,0.13);
  --orange:#fb923c;--orange-dim:rgba(251,146,60,0.13);
  --blue:#60a5fa;--blue-dim:rgba(96,165,250,0.13);
  --text-primary:#eeeeff;--text-secondary:#7878a0;--text-muted:#3e3e58;
  --font-sans:'Inter',system-ui,sans-serif;--font-mono:'JetBrains Mono',monospace;
  --sidebar-w:232px;--topbar-h:60px;
  --radius-sm:8px;--radius-md:14px;--radius-lg:18px;--radius-xl:22px;
}
html,body,#root{height:100%;background:var(--bg-base);font-family:var(--font-sans);color-scheme:dark;scroll-behavior:smooth;-webkit-text-size-adjust:100%}

/* Shell */
.ad-shell{display:flex;height:100vh;height:100dvh;overflow:hidden;background:var(--bg-base);position:relative}
.ad-shell::before{content:'';position:fixed;inset:0;pointer-events:none;z-index:0;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");opacity:.018}

/* Overlay */
.ad-overlay{display:none;position:fixed;inset:0;z-index:40;background:rgba(0,0,0,.6);backdrop-filter:blur(2px);opacity:0;transition:opacity .28s ease;pointer-events:none}
.ad-overlay.open{display:block;opacity:1;pointer-events:auto}

/* Sidebar */
.ad-sidebar{width:var(--sidebar-w);flex-shrink:0;display:flex;flex-direction:column;height:100%;overflow:hidden;background:var(--bg-surface);border-right:1px solid var(--border);position:relative;z-index:50;transition:transform .3s cubic-bezier(.4,0,.2,1);will-change:transform}
.ad-sidebar::after{content:'';position:absolute;top:0;right:0;bottom:0;width:1px;background:linear-gradient(to bottom,transparent 0%,var(--accent-glow) 35%,rgba(99,102,241,.08) 70%,transparent 100%);pointer-events:none}
.ad-logo{display:flex;align-items:center;gap:11px;padding:0 18px;height:var(--topbar-h);border-bottom:1px solid var(--border);flex-shrink:0}
.ad-logo-close{display:none;margin-left:auto;width:28px;height:28px;border-radius:7px;background:none;border:none;cursor:pointer;align-items:center;justify-content:center;color:var(--text-muted);transition:all .15s;flex-shrink:0}
.ad-logo-close:hover{background:var(--rose-dim);color:var(--rose)}
.ad-logo-icon{width:32px;height:32px;border-radius:10px;background:linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a78bfa 100%);display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 0 0 1px rgba(99,102,241,.4),0 4px 16px rgba(99,102,241,.35),0 0 32px rgba(99,102,241,.15)}
.ad-logo-text{line-height:1}
.ad-logo-text strong{font-size:13.5px;font-weight:800;background:linear-gradient(to right,#eeeeff,#a5b4fc);-webkit-background-clip:text;-webkit-text-fill-color:transparent;letter-spacing:-.4px}
.ad-logo-text span{font-size:9.5px;color:var(--text-muted);display:block;margin-top:3px;letter-spacing:.8px;text-transform:uppercase;font-weight:500}
.ad-nav{flex:1;min-height:0;overflow-y:auto;padding:18px 10px;display:flex;flex-direction:column;gap:22px;-webkit-overflow-scrolling:touch;scroll-behavior:smooth}
.ad-nav::-webkit-scrollbar{width:3px}
.ad-nav::-webkit-scrollbar-track{background:transparent}
.ad-nav::-webkit-scrollbar-thumb{background:rgba(99,102,241,.18);border-radius:99px}
.ad-nav-section-label{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--text-muted);padding:0 10px;margin-bottom:5px}
.ad-nav-items{display:flex;flex-direction:column;gap:1px}
.ad-nav-link{display:flex;align-items:center;gap:9px;padding:9px 10px;border-radius:var(--radius-sm);font-size:12.5px;font-weight:500;color:var(--text-secondary);text-decoration:none;transition:all .18s ease;position:relative;-webkit-tap-highlight-color:transparent;touch-action:manipulation}
.ad-nav-link:hover{background:var(--bg-hover);color:var(--text-primary)}
.ad-nav-link.active{background:linear-gradient(135deg,rgba(99,102,241,.18) 0%,rgba(139,92,246,.10) 100%);color:var(--accent-light);font-weight:600;box-shadow:inset 0 0 0 1px rgba(99,102,241,.15)}
.ad-nav-link.active::before{content:'';position:absolute;left:0;top:18%;bottom:18%;width:2.5px;border-radius:99px;background:linear-gradient(to bottom,var(--accent),var(--purple));box-shadow:0 0 10px var(--accent-glow)}
.ad-nav-link svg{flex-shrink:0;opacity:.65;transition:opacity .15s}
.ad-nav-link:hover svg,.ad-nav-link.active svg{opacity:1}
.ad-nav-chevron{margin-left:auto;opacity:.35}
.ad-user-card{flex-shrink:0;border-top:1px solid var(--border);padding:12px 10px;background:linear-gradient(to top,rgba(99,102,241,.04) 0%,transparent 100%)}
.ad-user-inner{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:var(--radius-sm);transition:background .15s}
.ad-user-inner:hover{background:var(--bg-hover)}
.ad-user-avatar{width:32px;height:32px;border-radius:50%;background:linear-gradient(135deg,var(--accent-dim),rgba(139,92,246,.2));border:1.5px solid rgba(99,102,241,.35);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:var(--accent-light);flex-shrink:0}
.ad-user-info{flex:1;min-width:0}
.ad-user-info strong{font-size:12px;font-weight:600;color:var(--text-primary);display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ad-user-info span{font-size:10px;color:var(--text-muted);display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:1px}
.ad-logout-btn{width:28px;height:28px;border-radius:7px;background:none;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:var(--text-muted);transition:all .15s;-webkit-tap-highlight-color:transparent}
.ad-logout-btn:hover{background:var(--rose-dim);color:var(--rose)}

/* Page */
.ad-page{flex:1;display:flex;flex-direction:column;overflow:hidden;position:relative;z-index:1;min-width:0}

/* Topbar */
.ad-topbar{flex-shrink:0;height:var(--topbar-h);background:rgba(10,10,22,.92);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;padding:0 24px;position:relative;gap:10px}
.ad-topbar::after{content:'';position:absolute;bottom:-1px;left:0;right:0;height:1px;background:linear-gradient(to right,transparent 0%,var(--accent-glow) 50%,transparent 100%)}
.ad-topbar-left{display:flex;align-items:center;gap:12px;min-width:0;flex:1}
.ad-topbar-title{font-size:15px;font-weight:700;color:var(--text-primary);letter-spacing:-.4px;white-space:nowrap}
.ad-topbar-sub{font-size:11px;color:var(--text-muted);margin-top:1px}
.ad-topbar-right{display:flex;align-items:center;gap:8px;flex-shrink:0}
.ad-hamburger{display:none;width:36px;height:36px;border-radius:var(--radius-sm);background:var(--bg-card);border:1px solid var(--border);cursor:pointer;align-items:center;justify-content:center;color:var(--text-secondary);transition:all .15s;flex-shrink:0;-webkit-tap-highlight-color:transparent}
.ad-hamburger:hover{background:var(--accent-dim);color:var(--accent-light);border-color:rgba(99,102,241,.25)}

/* Main scroll area */
.ad-main{flex:1;overflow-y:auto;padding:26px 28px;display:flex;flex-direction:column;gap:24px;-webkit-overflow-scrolling:touch;scroll-behavior:smooth;overscroll-behavior-y:contain}
.ad-main::-webkit-scrollbar{width:4px}
.ad-main::-webkit-scrollbar-track{background:transparent}
.ad-main::-webkit-scrollbar-thumb{background:rgba(99,102,241,.25);border-radius:99px}

/* ── Page header ── */
.pa-page-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap}
.pa-page-header-text h1{font-size:22px;font-weight:800;color:var(--text-primary);letter-spacing:-.6px}
.pa-page-header-text p{font-size:12px;color:var(--text-muted);margin-top:4px}

/* ── KPI row ── */
.pa-kpi-row{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}
.pa-kpi{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);padding:18px 20px;display:flex;align-items:center;gap:14px;transition:border-color .2s,box-shadow .2s,transform .18s;position:relative;overflow:hidden}
.pa-kpi:hover{border-color:var(--border-hi);box-shadow:0 4px 20px rgba(0,0,0,.3);transform:translateY(-1px)}
.pa-kpi::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--kpi-bar,transparent)}
.pa-kpi-icon{width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.pa-kpi-body{min-width:0;flex:1}
.pa-kpi-val{font-size:26px;font-weight:700;color:var(--text-primary);font-family:var(--font-mono);letter-spacing:-.9px;line-height:1}
.pa-kpi-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.9px;color:var(--text-muted);margin-top:4px}

/* ── Toolbar ── */
.pa-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.pa-search-wrap{position:relative}
.pa-search-wrap svg{position:absolute;left:11px;top:50%;transform:translateY(-50%);color:var(--text-muted);pointer-events:none}
.pa-search{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:7px 12px 7px 33px;font-size:12px;color:var(--text-primary);font-family:var(--font-sans);outline:none;width:220px;transition:border-color .18s,box-shadow .18s}
.pa-search::placeholder{color:var(--text-muted)}
.pa-search:focus{border-color:rgba(99,102,241,.4);box-shadow:0 0 0 3px rgba(99,102,241,.08)}
.pa-select{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:7px 30px 7px 11px;font-size:12px;color:var(--text-primary);font-family:var(--font-sans);outline:none;cursor:pointer;appearance:none;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%237878a0' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 9px center;transition:border-color .18s;min-width:140px}
.pa-select:focus{border-color:rgba(99,102,241,.4);box-shadow:0 0 0 3px rgba(99,102,241,.08)}

/* ── Buttons ── */
.pa-btn{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:var(--radius-sm);font-size:12px;font-weight:600;cursor:pointer;transition:all .18s;border:none;font-family:var(--font-sans);white-space:nowrap;-webkit-tap-highlight-color:transparent;text-decoration:none}
.pa-btn-primary{background:linear-gradient(135deg,var(--accent) 0%,#7c3aed 100%);color:#fff;box-shadow:0 2px 12px rgba(99,102,241,.35)}
.pa-btn-primary:hover{box-shadow:0 4px 20px rgba(99,102,241,.5);transform:translateY(-1px)}
.pa-btn-ghost{background:var(--bg-elevated);border:1px solid var(--border-hi);color:var(--text-secondary)}
.pa-btn-ghost:hover{background:var(--bg-hover);color:var(--text-primary);border-color:var(--border-glow)}
.pa-btn-danger{background:var(--rose-dim);border:1px solid rgba(251,113,133,.22);color:var(--rose)}
.pa-btn-danger:hover{background:rgba(251,113,133,.2);box-shadow:0 0 12px rgba(251,113,133,.2)}
.pa-btn-success{background:var(--green-dim);border:1px solid rgba(52,211,153,.22);color:var(--green)}
.pa-btn-success:hover{background:rgba(52,211,153,.2)}
.pa-btn-sm{padding:5px 11px;font-size:11px}
.pa-btn-icon{width:30px;height:30px;padding:0;justify-content:center;border-radius:7px}
.pa-btn-icon-sm{width:26px;height:26px;padding:0;justify-content:center;border-radius:6px}
.pa-btn:disabled{opacity:.4;cursor:not-allowed;transform:none!important}

/* ── Panel ── */
.pa-panel{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-md);overflow:hidden;display:flex;flex-direction:column;transition:border-color .2s}
.pa-panel:hover{border-color:var(--border-hi)}
.pa-panel-header{padding:16px 20px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;background:linear-gradient(to bottom,rgba(255,255,255,.02),transparent);flex-shrink:0}
.pa-panel-title{font-size:13px;font-weight:700;color:var(--text-primary);letter-spacing:-.2px}
.pa-panel-sub{font-size:11px;color:var(--text-muted);margin-top:2px}

/* ── Address grid + card ── */
.pa-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:14px;padding:20px}
.pa-card{background:var(--bg-elevated);border:1px solid var(--border);border-radius:var(--radius-md);padding:18px;display:flex;flex-direction:column;gap:13px;position:relative;transition:border-color .2s,box-shadow .2s,transform .18s}
.pa-card:hover{border-color:var(--border-hi);box-shadow:0 4px 20px rgba(0,0,0,.3);transform:translateY(-1px)}
.pa-card.inactive{opacity:.55}
.pa-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:10px}
.pa-coin{display:flex;align-items:center;gap:10px;min-width:0}
.pa-coin-icon{width:36px;height:36px;border-radius:10px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:12px;font-weight:800;font-family:var(--font-mono)}
.pa-coin-name{font-size:14px;font-weight:700;color:var(--text-primary)}
.pa-coin-network{font-size:10.5px;color:var(--text-muted);margin-top:1px;display:flex;align-items:center;gap:4px}
.pa-card-actions{display:flex;gap:5px;flex-shrink:0}
.pa-address-row{background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-sm);padding:9px 11px;display:flex;align-items:center;gap:8px}
.pa-address-text{flex:1;font-family:var(--font-mono);font-size:11px;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;letter-spacing:-.2px}
.pa-copy-btn{background:none;border:none;cursor:pointer;color:var(--text-muted);padding:3px;display:flex;align-items:center;justify-content:center;border-radius:5px;transition:all .15s;flex-shrink:0}
.pa-copy-btn:hover{color:var(--accent-light);background:var(--accent-dim)}
.pa-copy-btn.copied{color:var(--green)}
.pa-card-foot{display:flex;align-items:center;justify-content:space-between}
.pa-label{font-size:11px;color:var(--text-muted);display:flex;align-items:center;gap:5px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pa-status-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:99px;font-size:9.5px;font-weight:700;letter-spacing:.4px;text-transform:uppercase;border:1px solid;flex-shrink:0}
.pa-status-chip.active{background:var(--green-dim);color:var(--green);border-color:rgba(52,211,153,.2)}
.pa-status-chip.inactive{background:rgba(255,255,255,.04);color:var(--text-muted);border-color:var(--border-hi)}
.pa-dot-pulse{width:5px;height:5px;border-radius:50%;background:var(--green);box-shadow:0 0 5px var(--green);animation:pa-pulse 2s infinite;display:inline-block;flex-shrink:0}
@keyframes pa-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.7)}}

/* ── Empty / skeleton ── */
.pa-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:52px;color:var(--text-muted);gap:10px;font-size:12px;text-align:center}
.pa-empty svg{opacity:.35;margin-bottom:4px}
.pa-skel{height:10px;border-radius:5px;background:linear-gradient(90deg,var(--bg-hover) 25%,rgba(99,102,241,.05) 50%,var(--bg-hover) 75%);background-size:200% 100%;animation:pa-shimmer 1.8s infinite}
@keyframes pa-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}

/* ── Toast ── */
.pa-toast-wrap{position:fixed;bottom:24px;right:24px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none}
.pa-toast{display:flex;align-items:center;gap:10px;padding:12px 18px;border-radius:var(--radius-md);font-size:12.5px;font-weight:500;color:var(--text-primary);backdrop-filter:blur(12px);border:1px solid;animation:pa-toast-in .3s ease;pointer-events:auto;min-width:240px;max-width:360px;box-shadow:0 8px 32px rgba(0,0,0,.4)}
.pa-toast.success{background:rgba(14,14,28,.96);border-color:rgba(52,211,153,.25)}
.pa-toast.error{background:rgba(14,14,28,.96);border-color:rgba(251,113,133,.25)}
@keyframes pa-toast-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}

/* ── Modal ── */
.pa-modal-bg{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.65);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px;animation:pa-fade .22s ease}
@keyframes pa-fade{from{opacity:0}to{opacity:1}}
.pa-modal{background:var(--bg-surface);border:1px solid var(--border-hi);border-radius:var(--radius-xl);width:100%;max-width:480px;max-height:90vh;overflow-y:auto;-webkit-overflow-scrolling:touch;animation:pa-modal-up .24s cubic-bezier(.34,1.56,.64,1)}
@keyframes pa-modal-up{from{transform:translateY(16px) scale(.98);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}
.pa-modal-header{display:flex;align-items:center;justify-content:space-between;padding:20px 24px;border-bottom:1px solid var(--border);position:sticky;top:0;background:var(--bg-surface);z-index:1}
.pa-modal-title{font-size:15px;font-weight:700;color:var(--text-primary);letter-spacing:-.3px}
.pa-modal-body{padding:24px;display:flex;flex-direction:column;gap:16px}
.pa-modal-footer{padding:16px 24px;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:10px;position:sticky;bottom:0;background:var(--bg-surface);z-index:1}

/* ── Form ── */
.pa-field{display:flex;flex-direction:column;gap:6px}
.pa-label-field{font-size:11px;font-weight:600;color:var(--text-secondary);letter-spacing:.3px;text-transform:uppercase}
.pa-input{background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm);padding:9px 13px;font-size:13px;color:var(--text-primary);font-family:var(--font-sans);outline:none;transition:border-color .18s,box-shadow .18s;width:100%}
.pa-input.mono{font-family:var(--font-mono);font-size:12px}
.pa-input::placeholder{color:var(--text-muted)}
.pa-input:focus{border-color:rgba(99,102,241,.45);box-shadow:0 0 0 3px rgba(99,102,241,.09)}
.pa-input-hint{font-size:10.5px;color:var(--text-muted)}
.pa-field-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.pa-toggle-row{display:flex;align-items:center;justify-content:space-between;padding:11px 14px;background:var(--bg-elevated);border:1px solid var(--border-hi);border-radius:var(--radius-sm)}
.pa-toggle-row-text strong{font-size:12.5px;color:var(--text-primary);display:block}
.pa-toggle-row-text span{font-size:10.5px;color:var(--text-muted)}
.pa-switch{width:38px;height:22px;border-radius:99px;background:var(--bg-hover);border:1px solid var(--border-hi);position:relative;cursor:pointer;flex-shrink:0;transition:background .2s,border-color .2s}
.pa-switch.on{background:var(--green-dim);border-color:rgba(52,211,153,.4)}
.pa-switch-knob{position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:50%;background:var(--text-muted);transition:transform .2s,background .2s}
.pa-switch.on .pa-switch-knob{transform:translateX(16px);background:var(--green)}

/* ── Confirm ── */
.pa-confirm-bg{position:fixed;inset:0;z-index:300;background:rgba(0,0,0,.72);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px}
.pa-confirm{background:var(--bg-surface);border:1px solid var(--border-hi);border-radius:var(--radius-lg);padding:28px;max-width:380px;width:100%;animation:pa-modal-up .22s cubic-bezier(.34,1.56,.64,1)}
.pa-confirm-icon{width:44px;height:44px;border-radius:14px;display:flex;align-items:center;justify-content:center;margin-bottom:16px}
.pa-confirm h3{font-size:15px;font-weight:700;color:var(--text-primary);margin-bottom:8px}
.pa-confirm p{font-size:12.5px;color:var(--text-muted);line-height:1.55}
.pa-confirm-mono{font-family:var(--font-mono);font-size:11px;color:var(--text-secondary);background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-sm);padding:8px 10px;margin-top:10px;word-break:break-all}
.pa-confirm-actions{display:flex;gap:10px;margin-top:22px;justify-content:flex-end}

/* ── Responsive ── */
@media(min-width:1400px){:root{--sidebar-w:252px}.ad-main{padding:30px 36px}.ad-topbar{padding:0 36px}}
@media(max-width:1280px){.pa-kpi-row{grid-template-columns:repeat(2,1fr)}}
@media(max-width:1023px){:root{--sidebar-w:220px}.ad-main{padding:20px;gap:20px}.ad-topbar{padding:0 16px}}
@media(max-width:767px){
  :root{--topbar-h:56px}
  .ad-hamburger{display:flex}
  .ad-logo-close{display:flex}
  .ad-sidebar{position:fixed;top:0;left:0;bottom:0;height:100%;height:100dvh;overflow:hidden;transform:translateX(-100%);box-shadow:4px 0 32px rgba(0,0,0,.6)}
  .ad-sidebar.open{transform:translateX(0)}
  .ad-topbar{padding:0 14px}
  .ad-topbar-sub{display:none}
  .ad-main{padding:14px;gap:16px}
  .pa-kpi-row{grid-template-columns:repeat(2,1fr);gap:10px}
  .pa-grid{grid-template-columns:1fr;padding:14px}
  .pa-panel-header{flex-direction:column;align-items:stretch}
  .pa-toolbar{flex-direction:column;align-items:stretch}
  .pa-search{width:100%}
  .pa-select{width:100%}
  .pa-field-row{grid-template-columns:1fr}
  .pa-page-header{flex-direction:column;gap:12px}
  .pa-toast-wrap{left:14px;right:14px;bottom:14px}
}
@media(max-width:479px){
  .pa-kpi-row{grid-template-columns:1fr 1fr;gap:8px}
  .pa-kpi{padding:14px 12px;gap:10px}
  .pa-kpi-val{font-size:22px}
}
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Nav config ───────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  { label: "Overview",        items: [{ to: "/admin",               label: "Dashboard",      icon: LayoutDashboard, end: true }] },
  { label: "Users & Finance", items: [
    { to: "/admin/users",        label: "Users",        icon: Users        },
    { to: "/admin/wallets",      label: "Wallets",      icon: Wallet       },
    { to: "/admin/transactions", label: "Transactions", icon: Receipt      },
  ]},
  { label: "Trading", items: [
    { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
    { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
    { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
    { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
    { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
  ]},
  { label: "Communication", items: [
    { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
    { to: "/admin/notifications", label: "Notifications", icon: Bell           },
  ]},
  { label: "Settings", items: [
    { to: "/admin/subscriptions",     label: "Subscriptions",    icon: CreditCard },
    { to: "/admin/payment-addresses", label: "Payment Addresses", icon: MapPin    },
    { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
  ]},
];

function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick}
      className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmtDate = (s) => !s ? "—"
  : new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const truncateAddr = (addr, head = 10, tail = 8) =>
  !addr ? "" : addr.length <= head + tail + 3 ? addr : `${addr.slice(0, head)}…${addr.slice(-tail)}`;

// ─── Wallet icon theme ────────────────────────────────────────────────────────
// color stored in DB as a hex string e.g. "#fbbf24"
const hexToAlpha = (hex, a = 0.13) => {
  const c = hex?.replace("#", "") ?? "6366f1";
  const r = parseInt(c.slice(0,2),16), g = parseInt(c.slice(2,4),16), b = parseInt(c.slice(4,6),16);
  return `rgba(${r},${g},${b},${a})`;
};
const getWalletTheme = (wallet) => ({
  bg:    hexToAlpha(wallet?.color, 0.13),
  color: wallet?.color ?? "var(--accent-light)",
});

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toasts({ list }) {
  return (
    <div className="pa-toast-wrap">
      {list.map((t) => (
        <div key={t.id} className={`pa-toast ${t.type}`}>
          {t.type === "success" && <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />}
          {t.type === "error"   && <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          <span style={{ flex: 1 }}>{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Wallet card ─────────────────────────────────────────────────────────────
function AddressCard({ addr, onDelete, onToggleActive, onCopy, copiedId }) {
  const theme = getWalletTheme(addr);
  const isCopied = copiedId === addr.id;

  return (
    <div className={`pa-card${!addr.is_active ? " inactive" : ""}`}>
      <div className="pa-card-top">
        <div className="pa-coin">
          <div className="pa-coin-icon" style={{ background: theme.bg, color: theme.color }}>
            {addr.icon ? (
              <img src={addr.icon} alt={addr.symbol} style={{ width: 22, height: 22, objectFit: "contain" }}
                onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
              />
            ) : null}
            <span style={{ display: addr.icon ? "none" : "block" }}>{addr.symbol?.slice(0, 4)}</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="pa-coin-name">{addr.name ?? addr.symbol}</div>
            <div className="pa-coin-network">
              <Hash size={9} />{addr.symbol}
              {addr.network && <> · {addr.network}</>}
              {addr.is_default && (
                <span style={{ marginLeft: 4, background: "var(--accent-dim)", color: "var(--accent-light)", borderRadius: 4, padding: "1px 5px", fontSize: 9, fontWeight: 700, letterSpacing: ".4px", textTransform: "uppercase" }}>default</span>
              )}
            </div>
          </div>
        </div>
        <div className="pa-card-actions">
          <button
            className="pa-btn pa-btn-ghost pa-btn-icon-sm"
            title={addr.is_active ? "Deactivate" : "Activate"}
            onClick={() => onToggleActive(addr)}
          >
            {addr.is_active ? <Power size={12} /> : <PowerOff size={12} />}
          </button>
          <button className="pa-btn pa-btn-danger pa-btn-icon-sm" title="Delete address" onClick={() => onDelete(addr)}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div className="pa-address-row">
        <span className="pa-address-text" title={addr.address}>{addr.address}</span>
        <button className={`pa-copy-btn${isCopied ? " copied" : ""}`} title="Copy address" onClick={() => onCopy(addr)}>
          {isCopied ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>

      <div className="pa-card-foot">
        <span className="pa-label" title={addr.symbol}>
          <Coins size={11} />{addr.symbol}
        </span>
        <span className={`pa-status-chip ${addr.is_active ? "active" : "inactive"}`}>
          {addr.is_active && <span className="pa-dot-pulse" />}
          {addr.is_active ? "Active" : "Inactive"}
        </span>
      </div>
    </div>
  );
}

// ─── Add wallet modal ─────────────────────────────────────────────────────────
const BLANK = { symbol: "", name: "", network: "", address: "", icon: "", color: "#6366f1", is_default: false, is_active: true };

function AddAddressModal({ onClose, onSaved, addToast }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function save() {
    if (!form.symbol.trim()) { addToast("Symbol is required", "error"); return; }
    if (!form.name.trim())   { addToast("Name is required", "error"); return; }
    if (!form.address.trim()) { addToast("Wallet address is required", "error"); return; }
    setSaving(true);
    const payload = {
      symbol:     form.symbol.trim().toUpperCase(),
      name:       form.name.trim(),
      network:    form.network.trim() || form.symbol.trim().toUpperCase(),
      address:    form.address.trim(),
      icon:       form.icon.trim() || "",
      color:      form.color || "#6366f1",
      is_default: form.is_default,
      is_active:  form.is_active,
    };
    const { error } = await supabase.from("platform_crypto_wallets").insert(payload);
    setSaving(false);
    if (error) { addToast(error.message, "error"); return; }
    addToast(`${payload.symbol} wallet added`, "success");
    onSaved();
  }

  return (
    <div className="pa-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pa-modal">
        <div className="pa-modal-header">
          <span className="pa-modal-title">Add crypto wallet</span>
          <button className="pa-btn pa-btn-ghost pa-btn-icon" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="pa-modal-body">

          <div className="pa-field-row">
            <div className="pa-field">
              <label className="pa-label-field">Symbol *</label>
              <input className="pa-input mono" placeholder="e.g. BTC, ETH, USDT"
                value={form.symbol} onChange={(e) => set("symbol", e.target.value.toUpperCase())} />
            </div>
            <div className="pa-field">
              <label className="pa-label-field">Color</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)}
                  style={{ width: 38, height: 36, border: "1px solid var(--border-hi)", borderRadius: 7, background: "var(--bg-elevated)", cursor: "pointer", padding: 3 }} />
                <input className="pa-input mono" style={{ flex: 1 }} placeholder="#6366f1"
                  value={form.color} onChange={(e) => set("color", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="pa-field">
            <label className="pa-label-field">Name *</label>
            <input className="pa-input" placeholder="e.g. Bitcoin, Ethereum, Tether"
              value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>

          <div className="pa-field">
            <label className="pa-label-field">Network</label>
            <input className="pa-input" placeholder="e.g. Bitcoin, ERC20, TRC20, BEP20"
              value={form.network} onChange={(e) => set("network", e.target.value)} />
            <span className="pa-input-hint">Leave blank to use the symbol as the network name</span>
          </div>

          <div className="pa-field">
            <label className="pa-label-field">Wallet address *</label>
            <input className="pa-input mono" placeholder="e.g. bc1q… / 0x… / T9y…"
              value={form.address} onChange={(e) => set("address", e.target.value)} />
            <span className="pa-input-hint">Shown to users when they deposit {form.symbol || "this coin"}</span>
          </div>

          <div className="pa-field">
            <label className="pa-label-field">Icon URL</label>
            <input className="pa-input" placeholder="https://… (optional)"
              value={form.icon} onChange={(e) => set("icon", e.target.value)} />
            <span className="pa-input-hint">Optional — coin logo URL. Falls back to symbol text if blank</span>
          </div>

          <div className="pa-toggle-row">
            <div className="pa-toggle-row-text">
              <strong>Default wallet</strong>
              <span>Set as the primary address for this symbol</span>
            </div>
            <div className={`pa-switch${form.is_default ? " on" : ""}`} onClick={() => set("is_default", !form.is_default)}>
              <div className="pa-switch-knob" />
            </div>
          </div>

          <div className="pa-toggle-row">
            <div className="pa-toggle-row-text">
              <strong>Active</strong>
              <span>Visible to users for deposits</span>
            </div>
            <div className={`pa-switch${form.is_active ? " on" : ""}`} onClick={() => set("is_active", !form.is_active)}>
              <div className="pa-switch-knob" />
            </div>
          </div>

        </div>

        <div className="pa-modal-footer">
          <button className="pa-btn pa-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="pa-btn pa-btn-primary" onClick={save} disabled={saving}>
            {saving ? "Adding…" : "Add wallet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm delete dialog ────────────────────────────────────────────────────
function ConfirmDeleteAddress({ addr, onCancel, onConfirm, busy }) {
  return (
    <div className="pa-confirm-bg">
      <div className="pa-confirm">
        <div className="pa-confirm-icon" style={{ background: "var(--rose-dim)", border: "1px solid rgba(251,113,133,.2)" }}>
          <Trash2 size={18} style={{ color: "var(--rose)" }} />
        </div>
        <h3>Delete {addr.symbol} wallet?</h3>
        <p>This wallet address will be permanently removed and will no longer appear to users for deposits.</p>
        <div className="pa-confirm-mono">{addr.address}</div>
        <div className="pa-confirm-actions">
          <button className="pa-btn pa-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="pa-btn pa-btn-danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Deleting…" : "Delete address"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────
export default function AdminPaymentAddresses() {
  const navigate = useNavigate();

  // auth
  const [session,    setSession]    = useState(null);
  const [sesLoading, setSesLoading] = useState(true);

  // mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  // addresses
  const [addresses,    setAddresses]    = useState([]);
  const [loading,       setLoading]      = useState(true);
  const [search,        setSearch]       = useState("");
  const [fCurrency,     setFCurrency]    = useState("all");
  const [fStatus,       setFStatus]      = useState("all");

  // modals
  const [addOpen,    setAddOpen]    = useState(false);
  const [deleteAddr, setDeleteAddr] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [copiedId,   setCopiedId]   = useState(null);

  // toasts
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!s) { navigate("/admin/login", { replace: true }); return; }
      setSession(s); setSesLoading(false); fetchAddresses();
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      if (!s) navigate("/admin/login", { replace: true });
      else setSession(s);
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchAddresses() {
    setLoading(true);
    const { data, error } = await supabase
      .from("platform_crypto_wallets")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) addToast(error.message, "error");
    setAddresses(data ?? []);
    setLoading(false);
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  async function doDelete() {
    setDeleteBusy(true);
    const { error } = await supabase.from("platform_crypto_wallets").delete().eq("id", deleteAddr.id);
    setDeleteBusy(false);
    if (error) { addToast(error.message, "error"); return; }
    addToast(`${deleteAddr.symbol} wallet deleted`, "success");
    setDeleteAddr(null);
    fetchAddresses();
  }

  // ── Toggle active ─────────────────────────────────────────────────────────
  async function toggleActive(addr) {
    const { error } = await supabase
      .from("platform_crypto_wallets")
      .update({ is_active: !addr.is_active })
      .eq("id", addr.id);
    if (error) { addToast(error.message, "error"); return; }
    addToast(`${addr.symbol} wallet ${addr.is_active ? "deactivated" : "activated"}`, "success");
    fetchAddresses();
  }

  // ── Copy ──────────────────────────────────────────────────────────────────
  async function copyAddress(addr) {
    try {
      await navigator.clipboard.writeText(addr.address);
      setCopiedId(addr.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      addToast("Couldn't copy to clipboard", "error");
    }
  }

  async function handleLogout() { await supabase.auth.signOut(); }

  const adminEmail    = session?.user?.email ?? "admin@vantrexmarket.io";
  const adminInitials = adminEmail.slice(0, 2).toUpperCase();

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filtered = addresses.filter((a) => {
    if (fCurrency !== "all" && a.symbol !== fCurrency) return false;
    if (fStatus === "active" && !a.is_active) return false;
    if (fStatus === "inactive" && a.is_active) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = `${a.symbol} ${a.name ?? ""} ${a.network ?? ""} ${a.address}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const currenciesPresent = [...new Set(addresses.map((a) => a.symbol))];
  const stats = {
    total:    addresses.length,
    active:   addresses.filter((a) => a.is_active).length,
    inactive: addresses.filter((a) => !a.is_active).length,
    coins:    currenciesPresent.length,
  };

  // ── Loading gate ──────────────────────────────────────────────────────────
  if (sesLoading) return (
    <>
      <StyleInjector />
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg-base)", flexDirection: "column", gap: 14 }}>
        <div style={{ width: 38, height: 38, borderRadius: 11, background: "linear-gradient(135deg,#6366f1,#a78bfa)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Shield size={17} color="#fff" />
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Verifying session…</div>
      </div>
    </>
  );

  const kpis = [
    { label: "Total wallets",  val: stats.total,    icon: Wallet,    iconBg: "var(--accent-dim)",        iconColor: "var(--accent-light)", bar: "linear-gradient(to right,#6366f1,#8b5cf6)" },
    { label: "Active",         val: stats.active,   icon: Power,     iconBg: "var(--green-dim)",          iconColor: "var(--green)",        bar: "linear-gradient(to right,#34d399,#059669)" },
    { label: "Inactive",       val: stats.inactive, icon: PowerOff,  iconBg: "rgba(255,255,255,.05)",     iconColor: "var(--text-muted)",   bar: "rgba(255,255,255,.08)" },
    { label: "Currencies",     val: stats.coins,    icon: Coins,     iconBg: "var(--cyan-dim)",           iconColor: "var(--cyan)",         bar: "linear-gradient(to right,#22d3ee,#0891b2)" },
  ];

  return (
    <>
      <StyleInjector />
      <Toasts list={toasts} />

      {addOpen && (
        <AddAddressModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); fetchAddresses(); }}
          addToast={addToast}
        />
      )}
      {deleteAddr && (
        <ConfirmDeleteAddress
          addr={deleteAddr}
          onCancel={() => setDeleteAddr(null)}
          onConfirm={doDelete}
          busy={deleteBusy}
        />
      )}

      <div className="ad-shell">
        {/* Overlay */}
        <div className={`ad-overlay${sidebarOpen ? " open" : ""}`} onClick={closeSidebar} aria-hidden="true" />

        {/* Sidebar */}
        <aside className={`ad-sidebar${sidebarOpen ? " open" : ""}`}>
          <div className="ad-logo">
            <div className="ad-logo-icon"><Shield size={13} color="#fff" /></div>
            <div className="ad-logo-text">
              <strong>vantrexmarket</strong>
              <span>Admin panel</span>
            </div>
            <button className="ad-logo-close" onClick={closeSidebar}><X size={14} /></button>
          </div>
          <nav className="ad-nav">
            {NAV_SECTIONS.map((sec) => (
              <div key={sec.label}>
                <p className="ad-nav-section-label">{sec.label}</p>
                <div className="ad-nav-items">
                  {sec.items.map((item) => (
                    <SidebarLink key={item.to} {...item} onClick={closeSidebar} />
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="ad-user-card">
            <div className="ad-user-inner">
              <div className="ad-user-avatar">{adminInitials}</div>
              <div className="ad-user-info">
                <strong>Super Admin</strong>
                <span>{adminEmail}</span>
              </div>
              <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
                <LogOut size={13} />
              </button>
            </div>
          </div>
        </aside>

        {/* Page */}
        <div className="ad-page">

          {/* Topbar */}
          <header className="ad-topbar">
            <div className="ad-topbar-left">
              <button className="ad-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                <Menu size={16} />
              </button>
              <div>
                <div className="ad-topbar-title">Crypto Wallets</div>
                <div className="ad-topbar-sub">Manage platform deposit wallets</div>
              </div>
            </div>
            <div className="ad-topbar-right">
              <button className="pa-btn pa-btn-ghost pa-btn-sm" onClick={fetchAddresses} title="Refresh">
                <RefreshCw size={12} /> <span style={{ fontSize: 11 }}>Refresh</span>
              </button>
              <button className="pa-btn pa-btn-primary pa-btn-sm" onClick={() => setAddOpen(true)}>
                <Plus size={13} /> Add wallet
              </button>
            </div>
          </header>

          {/* Main */}
          <main className="ad-main">

            {/* Page heading */}
            <div className="pa-page-header">
              <div className="pa-page-header-text">
                <h1>Crypto Wallets</h1>
                <p>Platform wallets shown to users for deposits. Add, deactivate, or remove wallets per currency.</p>
              </div>
            </div>

            {/* KPI row */}
            <div className="pa-kpi-row">
              {kpis.map(({ label, val, icon: Icon, iconBg, iconColor, bar }) => (
                <div key={label} className="pa-kpi" style={{ "--kpi-bar": bar }}>
                  <div className="pa-kpi-icon" style={{ background: iconBg }}>
                    <Icon size={17} style={{ color: iconColor }} />
                  </div>
                  <div className="pa-kpi-body">
                    <div className="pa-kpi-val">{val.toLocaleString()}</div>
                    <div className="pa-kpi-label">{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Addresses panel */}
            <div className="pa-panel">
              <div className="pa-panel-header">
                <div>
                  <div className="pa-panel-title">Crypto wallets</div>
                  <div className="pa-panel-sub">
                    {filtered.length.toLocaleString()} of {addresses.length.toLocaleString()} wallet{addresses.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="pa-toolbar">
                  <div className="pa-search-wrap">
                    <Search size={12} />
                    <input
                      className="pa-search"
                      placeholder="Search address, symbol, name…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <select className="pa-select" value={fCurrency} onChange={(e) => setFCurrency(e.target.value)}>
                    <option value="all">All currencies</option>
                    {currenciesPresent.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select className="pa-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="pa-grid">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="pa-card" style={{ gap: 12 }}>
                      {[60, 90, 40].map((w, j) => (
                        <div key={j} className="pa-skel" style={{ width: `${w}%`, height: j === 1 ? 34 : 12 }} />
                      ))}
                    </div>
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="pa-empty">
                  <Wallet size={32} />
                  <div style={{ fontWeight: 600, color: "var(--text-secondary)" }}>
                    {addresses.length === 0 ? "No crypto wallets yet" : "No wallets match your filters"}
                  </div>
                  <div>
                    {addresses.length === 0
                      ? "Add a wallet address so users have somewhere to send deposits."
                      : "Try adjusting your search or filters."}
                  </div>
                  {addresses.length === 0 && (
                    <button className="pa-btn pa-btn-primary" style={{ marginTop: 8 }} onClick={() => setAddOpen(true)}>
                      <Plus size={13} /> Add first wallet
                    </button>
                  )}
                </div>
              ) : (
                <div className="pa-grid">
                  {filtered.map((a) => (
                    <AddressCard
                      key={a.id}
                      addr={a}
                      onDelete={(addr) => setDeleteAddr(addr)}
                      onToggleActive={toggleActive}
                      onCopy={copyAddress}
                      copiedId={copiedId}
                    />
                  ))}
                </div>
              )}
            </div>

          </main>
        </div>
      </div>
    </>
  );
}